import React, {useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'
import {
    FormControl,
    FormLabel,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    VisuallyHidden
} from '@chakra-ui/react'

import {Button} from '@salesforce/retail-react-app/app/components/shared/ui/Button'
import {ChevronDownIcon, LoaderIcon} from '@app/components/custom-icons'
import LocaleText from '@app/components/locale-text'
import useMultiSite from '@app/hooks/use-multi-site'
import {useDeliveryCountryDecision} from '@app/custom-contexts'
import {useLocation} from 'react-router-dom'

/**
 * Shows the language confirmation modal suggested by the SSR delivery decision.
 *
 * The modal lets the shopper keep the current locale or navigate to the same
 * page under another supported locale for the active site.
 *
 * @component
 * @param {object} props - Component props.
 * @param {boolean} props.isOpen - Whether the modal is visible.
 * @param {Function} props.onClose - Called when the modal is dismissed or completed.
 * @returns {React.ReactElement} Locale selector modal.
 */
const LocaleSelectorModal = ({isOpen, onClose}) => {
    const {site, locale, buildUrl, setLocale} = useMultiSite()
    const location = useLocation()
    const deliveryCountryDecision = useDeliveryCountryDecision()
    const suggestedLocaleId = deliveryCountryDecision?.suggestedLocale || locale?.id
    const [selectedLocaleId, setSelectedLocaleId] = useState(suggestedLocaleId)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        setSelectedLocaleId(suggestedLocaleId)
    }, [suggestedLocaleId])

    const localeOptions = useMemo(
        () =>
            (site?.l10n?.supportedLocales || []).map((supportedLocale) => (
                <option key={supportedLocale.id} value={supportedLocale.id}>
                    <LocaleText shortCode={supportedLocale.id} />
                </option>
            )),
        [site?.l10n?.supportedLocales]
    )

    /**
     * Tracks the shopper-selected locale from the modal select field.
     *
     * @param {React.ChangeEvent<HTMLSelectElement>} event - Select change event.
     */
    const handleLocaleChange = (event) => {
        setSelectedLocaleId(event.target.value)
    }

    /**
     * Applies the selected locale and navigates to the equivalent localized URL.
     */
    const handleConfirm = () => {
        const selectedLocale = site?.l10n?.supportedLocales?.find(
            (supportedLocale) => supportedLocale.id === selectedLocaleId
        )

        if (!selectedLocale || selectedLocale.id === locale?.id) {
            onClose()
            return
        }

        setIsLoading(true)
        setLocale(selectedLocale)

        const pathWithoutSiteAndLocale = location.pathname
            .replace(new RegExp(`^/${site.alias}`), '')
            .replace(new RegExp(`^/${locale.alias}`), '')
        const nextPath = pathWithoutSiteAndLocale || '/home'

        window.location.href = buildUrl(
            `${nextPath}${location.search || ''}`,
            site.alias || site.id,
            selectedLocale.alias || selectedLocale.id
        )
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="small" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalCloseButton />
                <ModalHeader>
                    <FormattedMessage
                        id="locale_selector_modal.title"
                        defaultMessage="CONFIRM YOUR LANGUAGE"
                    />
                </ModalHeader>
                <ModalBody>
                    <FormControl variant="floating">
                        <Select
                            onChange={handleLocaleChange}
                            id="selectLocale"
                            variant="basic"
                            value={selectedLocaleId || ''}
                            icon={<ChevronDownIcon />}
                        >
                            {localeOptions}
                        </Select>
                        <VisuallyHidden>
                            <FormLabel htmlFor="selectLocale">
                                <FormattedMessage
                                    id="locale_selector_modal.label"
                                    defaultMessage="Language"
                                />
                            </FormLabel>
                        </VisuallyHidden>
                    </FormControl>
                </ModalBody>
                <ModalFooter width="full" p="0">
                    <Button
                        variant="primary"
                        onClick={handleConfirm}
                        isLoading={isLoading}
                        isDisabled={!selectedLocaleId}
                        spinner={<LoaderIcon size={8} color="white" />}
                        w="full"
                    >
                        <FormattedMessage
                            id="country_selector_modal.button"
                            defaultMessage="Confirm"
                        />
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}

LocaleSelectorModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
}

export default LocaleSelectorModal
